import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, Elevation, Tag, Button, Icon, NonIdealState, Spinner, Switch,
  Dialog, DialogBody, DialogFooter, FormGroup, InputGroup, TextArea, Callout,
} from "@blueprintjs/core";
import { api, type BlueprintListItem } from "../lib/api";

const STATUS_MAP: Record<string, { intent: "none" | "primary" | "success" | "warning" | "danger"; label: string }> = {
  draft: { intent: "none", label: "Draft" },
  planning: { intent: "warning", label: "Planning" },
  strategy_review: { intent: "warning", label: "Strategy Review" },
  sample_review: { intent: "warning", label: "Sample Review" },
  sample_enriching: { intent: "primary", label: "Enriching Sample" },
  gathering: { intent: "primary", label: "Gathering" },
  enriching: { intent: "primary", label: "Enriching" },
  scoring: { intent: "primary", label: "Scoring" },
  completed: { intent: "success", label: "Completed" },
  failed_planning: { intent: "danger", label: "Failed" },
  failed_gathering: { intent: "danger", label: "Failed" },
  failed_enriching: { intent: "danger", label: "Failed" },
};

function statusTag(status: string) {
  const mapped = STATUS_MAP[status];
  if (mapped) return <Tag intent={mapped.intent} minimal round>{mapped.label}</Tag>;
  return <Tag minimal round>{status.replace(/_/g, " ")}</Tag>;
}

export function Blueprints() {
  const navigate = useNavigate();
  const [blueprints, setBlueprints] = useState<BlueprintListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [requirement, setRequirement] = useState("");

  const fetchBlueprints = () => {
    setLoading(true);
    const qs = showArchived ? "?include_archived=true" : "";
    api.get<BlueprintListItem[]>(`/blueprints/${qs}`)
      .then(setBlueprints)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlueprints(); }, [showArchived]);

  const handleCreate = async () => {
    if (!name.trim()) { setFormError("Name is required"); return; }
    setCreating(true);
    setFormError("");
    try {
      const bp = await api.post<{ id: string }>("/blueprints/", {
        name: name.trim(),
        description: description.trim() || null,
        requirement_text: requirement.trim() || null,
      });
      setShowCreate(false);
      setName(""); setDescription(""); setRequirement("");
      navigate(`/blueprints/${bp.id}`);
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="dash-header">
        <div>
          <h2>Blueprints</h2>
          <p>Define targeting requirements and generate lead lists automatically</p>
        </div>
        <div className="dash-header-actions">
          <Switch
            checked={showArchived}
            onChange={() => setShowArchived(!showArchived)}
            label="Show archived"
            style={{ margin: 0 }}
          />
          <Button icon="add" intent="primary" onClick={() => setShowCreate(true)}>
            New Blueprint
          </Button>
        </div>
      </div>

      {error && <Callout intent="danger" style={{ marginBottom: 16 }}>{error}</Callout>}

      {blueprints.length === 0 ? (
        <NonIdealState
          icon="map-create"
          title={showArchived ? "No blueprints found" : "No blueprints yet"}
          description="Create your first blueprint to define targeting criteria and start generating leads."
          action={<Button icon="add" intent="primary" onClick={() => setShowCreate(true)}>Create Blueprint</Button>}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 16 }}>
          {blueprints.map((bp) => (
            <Card
              key={bp.id}
              elevation={Elevation.ONE}
              interactive
              style={{ padding: 0, opacity: bp.is_archived ? 0.6 : 1 }}
              onClick={() => navigate(`/blueprints/${bp.id}`)}
            >
              <div style={{ padding: "20px 20px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{bp.name}</h4>
                  <div style={{ display: "flex", gap: 4 }}>
                    {bp.is_archived && <Tag intent="warning" minimal round>Archived</Tag>}
                    {statusTag(bp.status)}
                  </div>
                </div>
                <p style={{
                  margin: "0 0 12px", fontSize: 13, color: "#5c7080", lineHeight: 1.5,
                  overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                }}>
                  {bp.description || "No description"}
                </p>
              </div>
              <div style={{
                padding: "12px 20px", borderTop: "1px solid #dce0e5",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "#f8f9fa", borderRadius: "0 0 6px 6px",
              }}>
                <span style={{ fontSize: 12, color: "#5c7080" }}>
                  {bp.lead_count > 0 ? `${bp.lead_count} leads` : "No leads yet"} ·{" "}
                  {new Date(bp.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {bp.creator_name && ` · ${bp.creator_name}`}
                </span>
                <Icon icon="arrow-right" size={14} color="#5c7080" />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Blueprint" style={{ width: 520 }}>
        <DialogBody>
          {formError && <Callout intent="danger" style={{ marginBottom: 16 }}>{formError}</Callout>}
          <FormGroup label="Blueprint Name" labelInfo="(required)">
            <InputGroup
              placeholder="e.g. Healthcare SaaS Decision Makers"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Description">
            <TextArea
              fill
              placeholder="What is this blueprint targeting?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </FormGroup>
          <FormGroup label="Targeting Requirements" helperText="Describe your ideal lead profile in natural language">
            <TextArea
              fill
              placeholder="e.g. VP+ roles at healthcare SaaS companies with 50-500 employees in North America"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              rows={4}
            />
          </FormGroup>
        </DialogBody>
        <DialogFooter actions={
          <>
            <Button onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button intent="primary" onClick={handleCreate} loading={creating}>Create Blueprint</Button>
          </>
        } />
      </Dialog>
    </div>
  );
}
